import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Button from '../components/Button';
import SEO from '../components/SEO';
import servicesContent from '../data/servicesContent';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const [currentBanner, setCurrentBanner] = useState(0);

  const service = servicesContent.find((item) => item.slug === slug);

  // Use service-specific banner image if available, otherwise use default banners
  const banners = service?.bannerImage
    ? [service.bannerImage]
    : [
        '/banner1.jpg',
        '/banner2.jpg',
      ];

  useEffect(() => {
    // Reset to first banner when banners change
    setCurrentBanner(0);
    
    // Only run timer if there are multiple banners
    if (banners.length > 1) {
      const timer = setInterval(() => {
        setCurrentBanner((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [banners.length, service?.slug]);

  if (!service) {
    return (
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom sx={{ color: '#3c005a' }}>
            Service not found
          </Typography>
          <Typography variant="body1" paragraph>
            The requested service does not exist. Please explore our full list of offerings.
          </Typography>
          <Button component={Link} to="/services" size="large">
            View All Services
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <>
      <SEO
        title={`${service.title} | Thrana Pharmacovigilance Services`}
        description={service.summary || service.description}
        path={`/services/${service.slug}`}
      />
      <Box>
      <HeroSection>
        {banners.map((banner, index) => (
          <BannerImage
            key={`${service?.slug}-${banner}-${index}`}
            sx={{
              backgroundImage: `url(${banner})`,
              opacity: currentBanner === index ? 1 : 0,
              zIndex: currentBanner === index ? 1 : 0,
            }}
          />
        ))}
        <Overlay>
          <Container maxWidth="md">
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              {service.title}
            </Typography>
            {service.summary && (
              <Typography
                variant="h6"
                paragraph
                sx={{
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                  color: 'white',
                }}
              >
                {service.summary}
              </Typography>
            )}
          </Container>
        </Overlay>
      </HeroSection>

      <Container sx={{ py: 8 }}>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'justify' }}>
          {service.description}
        </Typography>

        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#3c005a' }}>
            What We Deliver
          </Typography>
          <Grid container spacing={3}>
            {service.features.map((feature) => (
              <Grid item xs={12} md={6} key={`${service.slug}-${feature}`}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="subtitle1" sx={{ color: '#3c005a', fontWeight: 600 }}>
                    {feature}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
            Ready to explore {service.title}?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Talk to our pharmacovigilance experts to tailor this service to your organization’s needs.
          </Typography>
          <Button component={Link} to="/contact" size="large">
            Contact Us
          </Button>
        </Box>
      </Container>
      </Box>
    </>
  );
};

export default ServiceDetailPage;

